import {
  notEmptyString,
} from '@populateui/utils';
import {
  createFilterOptions,
} from '@mui/material/Autocomplete';
import type {
  FilterOptionsState,
} from '@mui/material/useAutocomplete';
import isNil from 'lodash/isNil';

/** Accept either with or without a space before am/pm (output will always include a space) */
export type TimeString = `${string}:${string}${'' | ' '}${'am' | 'pm'}`;

export type TimeOption = {
  label: TimeString;
  minutes: number; // minutes since midnight
};

// Robust AM/PM detection at the end; supports: a, p, am, pm, a.m., p m, etc.
const ampmRegex = /(a\.?\s*m?\.?|p\.?\s*m?\.?)\s*$/i;

/** Convert minutes -> HH:MM am/pm (with a space) */
export const formatToReadableTime = (minutes: number): TimeString => {
  if (minutes < 0) return '' as TimeString;
  const hour24 = Math.floor(minutes / 60);
  const min = minutes % 60;
  const suffix = hour24 >= 12 ? 'pm' : 'am';
  let hour12 = hour24 % 12;
  if (hour12 === 0) hour12 = 12;
  const hourStr = hour12.toString().padStart(2, '0');
  const minStr = min.toString().padStart(2, '0');
  return `${hourStr}:${minStr} ${suffix}` as TimeString;
};

/**
 * Normalize time-ish user input (purely lexical; keeps parsing tolerant):
 * - Normalize unicode spaces -> regular space
 * - Map dot-like separators (· • ∙ ⋅) -> ':'
 * - Remove spaces around ':'
 * - Merge split AM/PM tokens: 'a m', 'a. m.', 'A   M' -> 'am' (same for pm)
 * - Join hour + suffix: '10 am' -> '10am'
 * - SPECIAL: if the *entire* string is bare hour + suffix (e.g. '7p', '7 pm'),
 *   return canonical 'HH:00 am/pm' **as a display string** (we keep this for UX parity)
 */
export const normalizeUserTimeInput = (raw: string): string => {
  let result = '';

  if (notEmptyString(raw)) {
    let s = String(raw);

    // unicode spaces -> normal
    s = s.replace(/[\u00A0\u1680\u2000-\u200B\u202F\u205F\u3000]/g, ' ');
    // dot-like separators -> ':'
    s = s.replace(/[\u00B7\u2022\u2219\u22C5\u30FB,]/g, ':');
    // trim spaces around ':'
    s = s.replace(/\s*:\s*/g, ':');
    // merge split am/pm tokens
    s = s.replace(/\b([ap])\s*\.?\s*m\.?\b/gi, (_m, ap: string) => `${ap.toLowerCase()}m`);
    // collapse remaining spaces
    s = s.replace(/\s+/g, ' ').trim();
    // '10 am' -> '10am'
    s = s.replace(/(\d{1,2})\s+([ap]m)\b/gi, (_m, hh: string, suf: string) => `${hh}${suf.toLowerCase()}`);

    // If it's a bare hour + suffix -> format HH:00 am/pm (canonical display)
    const bare = s.match(/^(\d{1,2})([ap])m?$/i);
    if (bare) {
      const hourNum = parseInt(bare[1], 10);
      if (hourNum >= 1 && hourNum <= 12) {
        const isPm = bare[2].toLowerCase() === 'p';
        const hour24 = (hourNum % 12) + (isPm ? 12 : 0);
        const minutes = hour24 * 60;
        result = formatToReadableTime(minutes);
      } else {
        result = '';
      }
    } else {
      // normalize trailing 'am'/'pm' case if already attached (space is handled by formatter later)
      result = s.replace(
        /^(\d{1,2}:\d{2})(am|pm)$/i,
        (_m, hm: string, ap: string) => `${hm}${ap.toLowerCase()}`,
      );
    }
  }

  return result;
};

/** Flexible parser tolerant to space/no-space before am/pm */
export const parseMinutesFromInput = (raw: string): number => {
  let result = -1;
  const sanitized = normalizeUserTimeInput(raw).toLowerCase();

  if (notEmptyString(raw) && notEmptyString(sanitized)) {
    let str = sanitized;
    // Allow an optional single space before am/pm (strip it for parsing)
    str = str.replace(/\s+(?=(am|pm)$)/, '');

    // Explicit AM/PM at end (also supports single 'a'/'p' via sanitize)
    let explicitModifier: 'am' | 'pm' | null = null;
    const modMatch = str.match(ampmRegex);
    if (modMatch) {
      const token = modMatch[1].toLowerCase();
      explicitModifier = token.includes('p') ? 'pm' : 'am';
      str = str.replace(ampmRegex, '').trim();
    }

    let hour: number;
    let minute: number;

    // Bare hour with explicit modifier
    if (result === -1 && explicitModifier && /^\d{1,2}$/.test(str)) {
      hour = parseInt(str, 10);
      if (hour < 1 || hour > 12) {
        result = -1;
      } else {
        let hour24 = hour % 12;
        if (explicitModifier === 'pm') hour24 += 12;
        result = hour24 * 60; // :00
      }
    }

    // HH:MM
    if (result === -1 && str.includes(':')) {
      const parts = str.split(':');
      if (parts.length !== 2) {
        result = -1;
      } else {
        const [h, m] = parts;
        hour = parseInt(h, 10);
        minute = parseInt(m, 10);
        if (Number.isNaN(hour) || Number.isNaN(minute) || minute < 0 || minute > 59) {
          result = -1;
        } else if (explicitModifier) {
          if (hour < 1 || hour > 12) {
            result = -1;
          } else {
            let hour24 = hour % 12;
            if (explicitModifier === 'pm') hour24 += 12;
            result = hour24 * 60 + minute;
          }
        } else if (hour < 0 || hour > 23) {
          result = -1;
        } else {
          result = hour * 60 + minute;
        }
      }
    }

    // HMM/HHMM
    if (result === -1 && /^\d{3,4}$/.test(str)) {
      const padded = str.padStart(4, '0');
      hour = parseInt(padded.slice(0, 2), 10);
      minute = parseInt(padded.slice(2), 10);
      if (Number.isNaN(hour) || Number.isNaN(minute) || minute < 0 || minute > 59) {
        result = -1;
      } else if (explicitModifier) {
        if (hour < 1 || hour > 12) {
          result = -1;
        } else {
          let hour24 = hour % 12;
          if (explicitModifier === 'pm') hour24 += 12;
          result = hour24 * 60 + minute;
        }
      } else if (hour > 23) {
        result = -1;
      } else if (hour > 12) {
        result = hour * 60 + minute;
      } else if (hour === 12) {
        result = 12 * 60 + minute;
      } else {
        result = (hour + 12) * 60 + minute;
      }
    }
  }

  return result;
};

export const toMinutes = (value?: string | number | null): number | undefined => {
  let result: number | undefined;

  if (typeof value === 'number') {
    result = value >= 0 ? value : undefined;
  } else if (typeof value === 'string') {
    const minutes = parseMinutesFromInput(value ?? '');
    result = minutes >= 0 ? minutes : undefined;
  }

  return isNil(value) ? undefined : result;
};

// Build all increments once for a given step in minutes, as tuples
export const generateTimes = (step: number): TimeOption[] => {
  const times: TimeOption[] = [];
  for (let minutes = 0; minutes < 24 * 60; minutes += step) {
    times.push({ label: formatToReadableTime(minutes), minutes });
  }
  return times;
};

/** Base filter that matches both spaced and unspaced suffixes */
export const filterOptionsBase = createFilterOptions<TimeOption>({
  trim: true,
  stringify: (option) => {
    const { label } = option;
    const noSpace = label.replace(/\s+(?=(am|pm)$)/i, ''); // remove only the space before am/pm
    return `${label} ${noSpace}`;
  },
});

/**
 * Filter that also prepends exact AM/PM candidates for compact input and biases by suffix.
 * NEW: Promote the exact/nearest parsed time to the top of the current result set.
 * NEW: When base filtering yields no matches but the input parses to a valid time
 *      that doesn't align to your step, return options strictly after the parsed time.
 */
export const filterTimeOptions = (
  options: TimeOption[],
  state: FilterOptionsState<TimeOption>,
) => {
  const { inputValue } = state;

  // Run your base filter first (matches both spaced and unspaced am/pm)
  const filtered = filterOptionsBase(options, state);

  const raw = normalizeUserTimeInput(inputValue || '').toLowerCase();
  const hasExplicitSuffix = /(a\.?\s*m?\.?|p\.?\s*m?\.?)\s*$/.test(raw);
  const suffix = hasExplicitSuffix ? raw.match(ampmRegex)?.[1]?.toLowerCase() : null;
  const wantsPm = suffix ? suffix.includes('p') : null;
  const wantsAm = suffix ? suffix.includes('a') : null;

  const prepend: TimeOption[] = [];
  const seen = new Set(filtered.map((o) => o.label));

  const maybePrependCandidates = (hhmm: string, prefer: 'am' | 'pm' | null) => {
    const hh = (parseInt(hhmm.slice(0, 2), 10) % 12) || 12;
    const mm = hhmm.slice(2);
    const candAm = `${hh.toString().padStart(2, '0')}:${mm} am` as TimeString;
    const candPm = `${hh.toString().padStart(2, '0')}:${mm} pm` as TimeString;
    const amOpt = options.find((o) => o.label === candAm);
    const pmOpt = options.find((o) => o.label === candPm);
    const tmp: TimeOption[] = [];
    if (amOpt && !seen.has(amOpt.label)) tmp.push(amOpt);
    if (pmOpt && !seen.has(pmOpt.label)) tmp.push(pmOpt);
    if (prefer) {
      tmp.sort((a, b) => {
        const aEndsWithPrefer = a.label.endsWith(prefer) ? -1 : 1;
        return a.label.endsWith(prefer) === b.label.endsWith(prefer) ? 0 : aEndsWithPrefer;
      });
    }
    prepend.push(...tmp);
  };

  let result: TimeOption[] = filtered;
  const ds = raw.match(/^(\d{3,4})\s*(a|p)/i);

  // Pure numeric 3–4 digits → prepend AM/PM candidates
  if (/^\d{3,4}$/.test(raw)) {
    const padded = raw.padStart(4, '0');
    const preferPm = wantsPm ? 'pm' : null;
    maybePrependCandidates(padded, wantsAm ? 'am' : preferPm);
    if (prepend.length) {
      result = [...prepend, ...filtered];
    }
  } else if (ds) {
    // Digit + suffix (e.g., "1000a", "1000 p.m.") → prepend with hint priority
    const padded = ds[1].padStart(4, '0');
    const prefer = ds[2].toLowerCase() === 'p' ? 'pm' : 'am';
    maybePrependCandidates(padded, prefer);
    if (prepend.length) {
      result = [...prepend, ...filtered];
    }
  } else if (wantsAm || wantsPm) {
    // If user typed explicit AM/PM (including single-letter), bias ordering
    const amFirst = wantsAm ? -1 : 1;
    const biased = [...filtered].sort((a, b) => {
      const aIsAm = /am$/i.test(a.label);
      const bIsAm = /am$/i.test(b.label);
      if (aIsAm === bIsAm) return 0;
      return aIsAm ? amFirst : -amFirst;
    });
    if (biased.length) {
      result = biased;
    }
  }

  // Existing fallback: when no matches but parsed is valid,
  // return options strictly after the parsed time.
  if (result === filtered && filtered.length === 0 && inputValue) {
    const parsed = parseMinutesFromInput(inputValue);
    if (parsed >= 0) {
      const after = options.filter((o) => o.minutes >= parsed);
      result = after.length ? after : [];
    }
  }

  return result;
};

/** Format a duration (in minutes) like: "30 mins", "1 hr", "1.5 hrs", "2 hrs" */
export function formatDuration(mins: number): string {
  let result: string;

  if (mins < 60) {
    result = `${mins} mins`;
  } else {
    const hours = mins / 60;
    if (Number.isInteger(hours)) {
      result = `${hours} ${hours === 1 ? 'hr' : 'hrs'}`;
    } else {
      const str = Number(hours.toFixed(2)).toString();
      result = `${str} hrs`;
    }
  }

  return result;
}
